import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { PrimaryCTA, SecondaryLinkCTA } from "@/components/editorial/primitives";
import { JsonLd, breadcrumbSchema } from "@/components/seo/JsonLd";

const BEHAVIOUR_SHIFTS = [
  {
    from: "Search",
    to: "Delegate",
    body: "Customers will increasingly ask an agent to compare, decide and act on their behalf.",
  },
  {
    from: "Navigate",
    to: "State intent",
    body: "People will expect to describe what they need instead of learning your process.",
  },
  {
    from: "Repeat",
    to: "Be remembered",
    body: "Persistent context will make repeating history, preferences and circumstances feel broken.",
  },
  {
    from: "React",
    to: "Be anticipated",
    body: "The best service will solve needs before they become avoidable problems.",
  },
];

const CAPABILITIES = [
  {
    number: "01",
    title: "Customer & service",
    body: "We redesign how your customers are served around what AI now makes possible, then build it into the operation.",
    items: ["Customer journeys", "Service model", "Contact and support", "Cost-to-serve"],
    link: { to: "/future-customer-experience", label: "Customer experience" },
  },
  {
    number: "02",
    title: "Internal operations",
    body: "We rebuild the workflows, connectors and routines behind the business so the tools you already pay for do real work.",
    items: ["Workflow redesign", "System connectors", "AI Employees", "Governance and ownership"],
    link: { to: "/what-we-do", label: "What we do" },
  },
  {
    number: "03",
    title: "Private equity",
    body: "Forward-deployed engineers inside a portfolio company. Embedded, building and deploying in weeks, not quarters.",
    items: ["Portfolio companies", "Margin-critical workflows", "8-week embeds", "Full handover"],
    link: { to: "/private-equity", label: "Private equity" },
  },
];

const PATH = [
  ["Changing behaviour", "Understand how the relevant customers or end users will act differently."],
  ["Business consequence", "Translate that change into implications for experience, proposition and economics."],
  ["Current gap", "Assess what the business can deliver today and where its assumptions no longer hold."],
  ["Tailored intervention", "Shape one coherent approach around the problem—not a menu of packages."],
  ["Working proof", "Design, build and test the critical parts in reality."],
  ["Ownership", "Develop the people and practices, transfer the capability, and leave."],
];

const Index = () => (
  <>
    <Helmet>
      <title>Brick — Forward-deployed AI transformation</title>
      <meta
        name="description"
        content="Brick embeds senior operators inside established businesses and rebuilds how work gets done with AI — customer experience, operations and PE portfolio companies. Running systems, not decks."
      />
      <link rel="canonical" href="https://brickplease.com/" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Brick — Forward-deployed AI transformation" />
      <meta
        property="og:description"
        content="We embed, build running systems with AI, hand over and leave."
      />
      <meta property="og:url" content="https://brickplease.com/" />
      <meta name="twitter:card" content="summary_large_image" />
    </Helmet>

    <JsonLd data={breadcrumbSchema([{ name: "Home", path: "/" }])} />

    <div className="min-h-screen bg-[hsl(var(--bg-canvas))] text-[hsl(var(--fg-cream))]">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[200] focus:px-4 focus:py-2 focus:bg-[hsl(var(--accent-brick))] focus:text-[hsl(var(--fg-cream))]"
      >
        Skip to content
      </a>

      <NavBar />

      <main id="main">
        <section className="relative overflow-hidden px-6 pt-32 pb-20 md:pt-44 md:pb-28">
          <div
            aria-hidden="true"
            className="absolute inset-x-0 bottom-0 h-px hero-slit bg-gradient-to-r from-transparent via-[hsl(var(--accent-brick))] to-transparent opacity-60"
          />
          <div className="relative max-w-6xl mx-auto grid lg:grid-cols-[1.35fr_0.65fr] gap-14 lg:gap-20 items-end">
            <div>
              <Reveal>
                <p className="eyebrow text-[hsl(var(--accent-brick))]">Forward-deployed AI transformation</p>
              </Reveal>
              <Reveal delay={0.08}>
                <h1 className="display-xl mt-7 max-w-5xl">
                  Your customers and competitors will move before your{" "}
                  <span className="text-[hsl(var(--accent-brick))]">organisation does.</span>
                </h1>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="mt-8 max-w-2xl text-[18px] md:text-[21px] leading-relaxed text-[hsl(var(--fg-cream))]/75">
                  We embed senior operators inside your business and rebuild how work gets done with
                  AI. Running systems, not slide decks. Then we leave.
                </p>
              </Reveal>
              <Reveal delay={0.24}>
                <div className="mt-10 flex flex-wrap gap-4">
                  <PrimaryCTA label="Talk to Brick" modalTitle="Talk to Brick." />
                  <SecondaryLinkCTA to="/how-we-work" label="See how we work" />
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.24}>
              <aside className="border-l border-[hsl(var(--accent-brick))] pl-6 md:pl-8 pb-1">
                <p className="eyebrow text-[hsl(var(--fg-muted))]">The risk</p>
                <p className="mt-5 text-[22px] md:text-[27px] leading-[1.2] tracking-[-0.025em]">
                  82% usage. 100% cost. Far less than 82% results.
                </p>
                <p className="mt-5 text-[14px] leading-relaxed text-[hsl(var(--fg-cream))]/60">
                  You already bought AI. Your people didn't adapt. We fix that.
                </p>
              </aside>
            </Reveal>
          </div>
        </section>

        <section className="border-y border-[hsl(var(--border-hairline))] bg-[hsl(var(--panel))]">
          <div className="max-w-6xl mx-auto px-6 py-8 flex flex-wrap items-center justify-between gap-4">
            {["Embed", "Build", "Hand over", "Leave"].map((item, index) => (
              <div key={item} className="flex items-center gap-4">
                <span className="font-mono text-[11px] text-[hsl(var(--accent-brick))]">0{index + 1}</span>
                <span className="text-[13px] font-medium uppercase tracking-[0.12em]">{item}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="px-6 py-20 md:py-32">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-24">
              <div>
                <p className="eyebrow text-[hsl(var(--accent-brick))]">The behaviour shift</p>
                <h2 className="display-lg mt-4">
                  The interface between people and companies is being rewritten.
                </h2>
              </div>
              <p className="text-[17px] leading-relaxed text-[hsl(var(--fg-cream))]/70 lg:pt-8">
                The opportunity is not to bolt AI onto today's journeys. It is to ask which journeys,
                propositions and interactions should exist when context persists, effort disappears
                and customers can delegate work to their own agents.
              </p>
            </div>

            <div className="mt-14 border-t border-[hsl(var(--border-hairline))]">
              {BEHAVIOUR_SHIFTS.map((shift, index) => (
                <div
                  key={shift.from}
                  className="grid md:grid-cols-[80px_1fr_1fr] gap-4 md:gap-8 py-7 border-b border-[hsl(var(--border-hairline))] items-baseline"
                >
                  <span className="font-mono text-[11px] text-[hsl(var(--fg-muted))]">0{index + 1}</span>
                  <h3 className="text-[23px] md:text-[30px] font-medium tracking-[-0.03em]">
                    <span className="text-[hsl(var(--fg-cream))]/35 line-through decoration-1">{shift.from}</span>
                    <span className="mx-3 text-[hsl(var(--accent-brick))]">→</span>
                    {shift.to}
                  </h3>
                  <p className="text-[15px] leading-relaxed text-[hsl(var(--fg-cream))]/65">{shift.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 py-20 md:py-32 border-t border-[hsl(var(--border-hairline))] bg-[hsl(var(--panel))]">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-24">
            <div>
              <p className="eyebrow text-[hsl(var(--accent-brick))]">The incumbent advantage</p>
              <h2 className="display-lg mt-4">You do not need to start over.</h2>
            </div>
            <div>
              <p className="text-[20px] md:text-[25px] leading-[1.35] tracking-[-0.02em]">
                Established companies already have customers, trust, distribution, expertise,
                systems and operating knowledge.
              </p>
              <p className="mt-7 text-[16px] leading-relaxed text-[hsl(var(--fg-cream))]/68">
                The problem is that much of this advantage is organised around behaviour that is
                changing. Brick helps redesign what you already have around what is now possible.
              </p>
            </div>
          </div>
        </section>

        <section id="capabilities" className="px-6 py-20 md:py-32">
          <div className="max-w-6xl mx-auto">
            <p className="eyebrow text-[hsl(var(--accent-brick))]">Where we deploy</p>
            <h2 className="display-lg mt-4 max-w-4xl">
              One way of working. Three places it lands.
            </h2>

            <div className="mt-14 grid lg:grid-cols-3 border-t border-l border-[hsl(var(--border-hairline))]">
              {CAPABILITIES.map((capability) => (
                <article
                  key={capability.number}
                  className="p-7 md:p-9 border-r border-b border-[hsl(var(--border-hairline))]"
                >
                  <span className="font-mono text-[11px] text-[hsl(var(--accent-brick))]">{capability.number}</span>
                  <h3 className="mt-7 text-[24px] leading-tight font-medium tracking-[-0.025em]">
                    {capability.title}
                  </h3>
                  <p className="mt-5 text-[14px] leading-relaxed text-[hsl(var(--fg-cream))]/65">
                    {capability.body}
                  </p>
                  <ul className="mt-7 space-y-2 pt-6 border-t border-[hsl(var(--border-hairline))]">
                    {capability.items.map((item) => (
                      <li key={item} className="text-[12px] font-mono uppercase tracking-[0.08em] text-[hsl(var(--fg-cream))]/60">
                        <span className="text-[hsl(var(--accent-brick))]">▸</span> {item}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7">
                    <Link to={capability.link.to} className="text-[13px] font-medium text-[hsl(var(--accent-brick))] hover:underline">
                      {capability.link.label} →
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 py-20 md:py-32 border-t border-[hsl(var(--border-hairline))]">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-[0.75fr_1.25fr] gap-12 lg:gap-24">
            <div>
              <p className="eyebrow text-[hsl(var(--accent-brick))]">How it starts</p>
              <h2 className="display-lg mt-4">You bring the challenge.</h2>
              <p className="mt-6 text-[16px] leading-relaxed text-[hsl(var(--fg-cream))]/68">
                We assess what is changing, where you are now and what the right intervention
                requires. No product menu. No pre-shaped transformation programme.
              </p>
              <div className="mt-8">
                <SecondaryLinkCTA to="/how-we-work" label="See how we work" />
              </div>
            </div>
            <ol className="border-t border-[hsl(var(--border-hairline))]">
              {PATH.map(([title, body], index) => (
                <li
                  key={title}
                  className="grid grid-cols-[48px_1fr] md:grid-cols-[64px_0.7fr_1.3fr] gap-4 py-5 border-b border-[hsl(var(--border-hairline))]"
                >
                  <span className="font-mono text-[11px] text-[hsl(var(--accent-brick))]">0{index + 1}</span>
                  <strong className="text-[15px] font-medium">{title}</strong>
                  <p className="col-start-2 md:col-start-auto text-[14px] leading-relaxed text-[hsl(var(--fg-cream))]/60">
                    {body}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="px-6 py-20 md:py-32 border-t border-[hsl(var(--border-hairline))] bg-[hsl(var(--panel))]">
          <div className="max-w-6xl mx-auto grid md:grid-cols-[0.78fr_1.22fr] gap-10 md:gap-16 items-stretch">
            <div className="min-h-[420px] overflow-hidden">
              <img
                src="/lovable-uploads/testimonial-malin-640.webp"
                alt="Malin Gustavsson Kvarnström"
                className="w-full h-full object-cover grayscale-[20%]"
                loading="lazy"
              />
            </div>
            <blockquote className="flex flex-col justify-between py-3 md:py-10">
              <div>
                <p className="eyebrow text-[hsl(var(--accent-brick))]">Working proof</p>
                <p className="mt-8 text-[36px] md:text-[53px] leading-[1.03] tracking-[-0.045em]">
                  “I used to spend 3 hours researching one account. Now it takes{" "}
                  <span className="text-[hsl(var(--accent-brick))]">12 minutes.</span>”
                </p>
              </div>
              <footer className="mt-12 pt-6 border-t border-[hsl(var(--border-hairline))]">
                <cite className="not-italic text-[14px] font-medium">Malin Gustavsson Kvarnström</cite>
                <p className="mt-1 text-[12px] font-mono uppercase tracking-[0.08em] text-[hsl(var(--fg-muted))]">
                  Senior Account Executive · NiCE Cognigy
                </p>
              </footer>
            </blockquote>
          </div>
        </section>

        <section className="px-6 py-24 md:py-40 text-center border-t border-[hsl(var(--border-hairline))]">
          <div className="max-w-4xl mx-auto">
            <p className="eyebrow text-[hsl(var(--accent-brick))]">Build with them. Transfer ownership. Leave.</p>
            <h2 className="display-lg mt-5">What should your business become next?</h2>
            <p className="mt-7 mx-auto max-w-2xl text-[17px] leading-relaxed text-[hsl(var(--fg-cream))]/68">
              Tell us what is changing, what is not working or what you believe could be possible.
              We will help determine what the right intervention requires.
            </p>
            <div className="mt-10 flex justify-center">
              <PrimaryCTA label="Talk to Brick" modalTitle="Talk to Brick." />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  </>
);

export default Index;
